import { useCallback, useEffect, useState } from "react";
import { MapPin, Search, Tags } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { STATUSES, fmtDate } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { ReferralAdminDialog } from "@/components/admin/ReferralAdminDialog";
import { CategoryManager } from "@/components/admin/CategoryManager";

const STAT_CARDS = [["totali", "Totali", "text-[#12201A]"], ["nuova", "Nuove", "text-[#0F2D5E]"], ["in_verifica", "In verifica", "text-[#8A6100]"],
  ["inoltrata", "Inoltrate", "text-[#0277BD]"], ["conclusa", "Concluse", "text-[#1E6B24]"], ["rifiutata", "Rifiutate", "text-[#C62828]"]];

export const AdminReferrals = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState({ status: "all", category_id: "all", q: "" });
  const [selected, setSelected] = useState(null);
  const [catOpen, setCatOpen] = useState(false);

  const loadCats = useCallback(() => api.get("/admin/categories").then((r) => setCats(r.data)), []);
  const load = useCallback(async () => {
    const params = {};
    if (filters.status !== "all") params.status = filters.status;
    if (filters.category_id !== "all") params.category_id = filters.category_id;
    if (filters.q.trim()) params.q = filters.q.trim();
    const [r, s] = await Promise.all([api.get("/admin/referrals", { params }), api.get("/admin/stats")]);
    setItems(r.data);
    setStats(s.data);
  }, [filters]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => {
    const t = setTimeout(() => load().catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="admin-referrals">
      <div className="grid grid-cols-3 gap-3 lg:grid-cols-6">
        {STAT_CARDS.map(([k, label, cls]) => (
          <div key={k} className="rounded-2xl border border-[#E2E5DC] bg-white p-3 sm:p-4" data-testid={`stat-${k}`}>
            <p className={`font-display text-2xl font-extrabold sm:text-3xl ${cls}`}>{stats[k] ?? 0}</p>
            <p className="text-xs text-[#75867E]">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-3 md:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3.5 h-4 w-4 text-[#75867E]" />
          <Input placeholder="Cerca per nome, città, telefono, email..." className="h-11 pl-9" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="admin-search-input" />
        </div>
        <div className="grid grid-cols-2 gap-3 md:flex">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="h-11 md:w-40" data-testid="admin-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {Object.entries(STATUSES).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category_id} onValueChange={(v) => setFilters({ ...filters, category_id: v })}>
            <SelectTrigger className="h-11 md:w-52" data-testid="admin-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le tipologie</SelectItem>
              {cats.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={() => setCatOpen(true)} className="h-11 rounded-full border-[#0F2D5E] text-[#0F2D5E]" data-testid="manage-categories-btn">
          <Tags className="mr-2 h-4 w-4" /> Tipologie
        </Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E2E5DC] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#E2E5DC] bg-[#F4F5F0] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[#75867E] lg:grid">
          <span className="col-span-3">Persona</span><span className="col-span-3">Tipologia</span><span className="col-span-2">Città</span>
          <span className="col-span-2">Segnalatore</span><span className="col-span-1">Data</span><span className="col-span-1">Stato</span>
        </div>
        {!items.length && <p className="p-10 text-center text-sm text-[#75867E]" data-testid="admin-referrals-empty">Nessuna segnalazione trovata.</p>}
        <ul className="divide-y divide-[#E2E5DC]">
          {items.map((r) => (
            <li key={r.referral_id}>
              <button onClick={() => setSelected(r)} className="grid w-full gap-1 px-4 py-4 text-left transition-colors hover:bg-[#FBFBF8] sm:px-5 lg:grid-cols-12 lg:items-center lg:gap-4" data-testid={`admin-referral-row-${r.referral_id}`}>
                <div className="flex items-center justify-between gap-2 lg:col-span-3">
                  <span className="truncate font-semibold">{r.lead_name}</span>
                  <span className="lg:hidden"><StatusBadge status={r.status} /></span>
                </div>
                <span className="text-sm text-[#495850] lg:col-span-3">{r.category_name}</span>
                <span className="flex items-center gap-1 text-sm text-[#495850] lg:col-span-2"><MapPin className="h-3 w-3 lg:hidden" />{r.lead_city}</span>
                <span className="truncate text-xs text-[#75867E] lg:col-span-2 lg:text-sm">{r.user_name}</span>
                <span className="font-mono text-xs text-[#75867E] lg:col-span-1">{fmtDate(r.created_at)}</span>
                <span className="hidden lg:col-span-1 lg:block"><StatusBadge status={r.status} testId={`admin-status-${r.referral_id}`} /></span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <ReferralAdminDialog referral={selected} categories={cats} onClose={() => setSelected(null)} onSaved={(r) => { setSelected(r); load(); }} />
      <CategoryManager open={catOpen} onOpenChange={setCatOpen} categories={cats} onChange={loadCats} />
    </div>
  );
};
